import { useRef } from 'react';

import { useGetEquityDetail } from '#/api/generated/equity/equity';
import { CondensedTickerSummary } from '#/components/detail/CondensedTickerSummary';
import { DetailNotFoundState } from '#/components/detail/DetailNotFoundState';
import { DetailSectionNav } from '#/components/detail/DetailSectionNav';
import { DetailSkeleton } from '#/components/detail/DetailSkeleton';
import { EquityCategorySection } from '#/components/detail/EquityCategorySection';
import { EquityFilingsSection } from '#/components/detail/EquityFilingsSection';
import { EquityPerformanceSection } from '#/components/detail/EquityPerformanceSection';
import { EquitySummarySection } from '#/components/detail/EquitySummarySection';
import { EquityTopHoldersSection } from '#/components/detail/EquityTopHoldersSection';
import { LearnMoreSection } from '#/components/detail/LearnMoreSection';
import { DETAIL_SECTION_NAV } from '#/components/detail/sectionNav';
import { StaticLearnMoreResourcesSection } from '#/components/detail/StaticLearnMoreResourcesSection';
import type { DetailViewProps } from '#/components/detail/types';
import { ContentLayout } from '#/components/layout/ContentLayout';
import { Breadcrumbs } from '#/components/ui/Breadcrumbs';
import { ErrorBoundary } from '#/components/ui/ErrorBoundary';
import { QueryBoundary } from '#/components/ui/QueryBoundary';
import { useEquityDetailContent } from '#/content/equity-detail';
import { useDefinitionDrawer } from '#/context/DefinitionDrawerContext';
import { useBreadcrumbs } from '#/hooks/useBreadcrumbs';
import { useMeasureOnCommit } from '#/hooks/usePerformanceMeasure';
import { isSuccessResponse } from '#/lib/api-client';
import { SECTOR_SLUG_BY_LABEL } from '#/lib/category-navigation';
import { MDC_MEASURE } from '#/lib/performance';
import { getEquityETFPriceChangeDisplay } from '#/utils/priceChange';

import { getEquityBreadcrumbItems } from './getEquityBreadcrumbItems';

const SECTION_NAV = DETAIL_SECTION_NAV.equities;

export function EquityDetail({ id }: DetailViewProps) {
  const breadcrumbs = useBreadcrumbs();
  const contentRef = useRef<HTMLDivElement>(null);
  const priceSummaryRef = useRef<HTMLDivElement>(null);

  const query = useGetEquityDetail(id);
  const { content, definitions } = useEquityDetailContent();

  // Publish this page's glossary terms for tooltips and the drawer.
  useDefinitionDrawer(definitions);

  // Closes `mdc:data-visible` on the commit that swaps the loading state for
  // real detail data.
  useMeasureOnCommit(MDC_MEASURE.dataVisible, isSuccessResponse(query.data));

  return (
    <QueryBoundary
      query={query}
      loading={<DetailSkeleton category="equities" symbol={id} />}
      notFound={(error) => <DetailNotFoundState symbol={id} error={error} />}
    >
      {(response) => {
        const { percentChange, valueChange, periodLabel } =
          getEquityETFPriceChangeDisplay(response.data.overview);

        const breadcrumbItems = getEquityBreadcrumbItems(
          breadcrumbs,
          response.data.related?.sector,
        );

        return (
          <ContentLayout>
            <DetailSectionNav
              sections={SECTION_NAV}
              contentRef={contentRef}
              priceSummaryRef={priceSummaryRef}
              jumpTrack={{
                track: 'section_nav_jump',
                context: 'detail_section_nav',
              }}
              condensedSummary={
                <CondensedTickerSummary
                  overview={response.data.overview}
                  percentChange={percentChange}
                  valueChange={valueChange}
                  changePeriodLabel={periodLabel}
                />
              }
            />

            <div className="col-content flow flow-60" ref={contentRef}>
              <Breadcrumbs
                items={breadcrumbItems}
                dataTrack="breadcrumb_link"
                dataTrackContext="breadcrumb"
              />

              <ErrorBoundary>
                <EquitySummarySection
                  data={response.data.overview}
                  id={id}
                  priceSummaryRef={priceSummaryRef}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <EquityPerformanceSection
                  symbol={id}
                  data={response.data.performance}
                  description="Prices are refreshed every 15 minutes"
                  date={response.data.overview?.last_refreshed_timestamp}
                  priceIntro={content.priceIntro}
                  riskIntro={content.riskIntro}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <EquityFilingsSection
                  symbol={id}
                  intro={content.filingsIntro}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <EquityTopHoldersSection
                  data={response.data.top_fund_holders}
                  intro={content.topHoldersIntro}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <EquityCategorySection
                  sectorSlug={
                    response.data.related?.sector
                      ? SECTOR_SLUG_BY_LABEL[response.data.related.sector]
                      : undefined
                  }
                  industry={response.data.related?.industry}
                  marketCapCategory={
                    response.data.performance?.market_cap_category
                  }
                />
              </ErrorBoundary>

              {content.learnMoreArticles && (
                <ErrorBoundary>
                  <LearnMoreSection
                    title="Learn More About Equities"
                    articles={content.learnMoreArticles}
                    articleTrack="learn_more_article"
                    articleTrackContext="learn_more"
                  />
                </ErrorBoundary>
              )}

              <StaticLearnMoreResourcesSection
                id={content.learnMoreArticles ? undefined : 'learn-more'}
              />
            </div>
          </ContentLayout>
        );
      }}
    </QueryBoundary>
  );
}
